// src/components/chat/ProductHeader.js
/**
 * ProductHeader - Compact product context bar for chat
 * Shows product thumbnail, title, price, and action buttons
 * Minimal footprint to maximize chat readability
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Tag, ShoppingBag, Clock } from 'lucide-react';
import { formatCountdownDisplay } from '../../utils/formatExpiration';

function extractExpiryDate(product) {
  if (!product) return null;
  const raw =
    product?.expiry?.toDate?.() ||
    (product?.expiry ? new Date(product.expiry) : null) ||
    product?.expiresAt?.toDate?.() ||
    (product?.expiresAt ? new Date(product.expiresAt) : null) ||
    (product?.expiryDate ? new Date(product.expiryDate) : null) ||
    (product?.sellUntil ? new Date(product.sellUntil) : null);
  return raw && !isNaN(raw.getTime()) ? raw : null;
}

export default function ProductHeader({
  product,
  seller,
  onBack,
  onMakeOffer,
  onBuyNow,
  isLoading,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation(['inbox', 'common']);

  if (!product) return null;

  const price = product.price || product.amount;
  const images = product.images || (product.image ? [product.image] : []) || (product.photo ? [product.photo] : []);
  const image = images[product.coverIndex || 0] || images[0];
  const title = product.title || product.name || 'Product';
  const sellerName = seller?.displayName || seller?.name || 'Seller';
  const sellerId = seller?.userId || seller?.uid || seller?.id;
  const isSold = product.sold || product.status === 'sold';
  const expiryDate = extractExpiryDate(product);
  const timeRemaining = formatCountdownDisplay(expiryDate);
  const isExpired = timeRemaining === 'Expired';

  const formattedPrice = price ? `${Number(price).toLocaleString()} RWF` : '';

  return (
    <div>
      {/* Row 1: Back + product info + actions */}
      <div className="flex items-center gap-2 px-2 py-2 sm:px-3">
        {/* Back button */}
        <button
          onClick={onBack}
          className="flex-shrink-0 p-1.5 hover:bg-muted rounded-lg transition text-foreground"
          title="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Product thumbnail */}
        {image && (
          <button
            onClick={() => product.id && navigate(`/product/${product.id}`)}
            className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-muted border border-border hover:ring-2 hover:ring-green-500/30 transition-all"
          >
            <img src={image} alt={title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          </button>
        )}

        {/* Title + Price + Seller */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground truncate">{title}</h3>
            {formattedPrice && (
              <span className={`text-sm font-bold flex-shrink-0 ${isSold ? 'text-muted-foreground line-through' : 'text-green-600 dark:text-green-400'}`}>
                {formattedPrice}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <button
              onClick={() => sellerId && navigate(`/profile/${sellerId}`)}
              className="hover:text-foreground transition-colors truncate"
            >
              {sellerName}
            </button>
            {timeRemaining && (
              <span className={`flex items-center gap-0.5 ${isExpired ? 'text-red-500' : ''}`}>
                <Clock className="w-3 h-3" />
                {timeRemaining}
              </span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        {!isSold && !isExpired && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {onMakeOffer && (
              <button
                onClick={onMakeOffer}
                disabled={isLoading}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-foreground/20 text-foreground hover:bg-muted transition-colors disabled:opacity-50"
              >
                {t('inbox:makeOffer', 'Make an offer')}
              </button>
            )}
            {onBuyNow && (
              <button
                onClick={onBuyNow}
                disabled={isLoading}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors disabled:opacity-50"
              >
                {t('inbox:buyNow', 'Buy')}
              </button>
            )}
          </div>
        )}

        {isSold && (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-muted text-muted-foreground flex-shrink-0">
            Sold
          </span>
        )}
        {isExpired && !isSold && (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex-shrink-0">
            {t('inbox:expired', 'Expired')}
          </span>
        )}
      </div>
    </div>
  );
}
